import React from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Alert } from '@ublend-npm/aulaui-next';

import { FormWrapper } from '../FormWrapper';

import {
  TextField,
  Title,
  AlertContainer,
  TextfieldContainer,
  SubmitContainer,
  SignupButtonContainer,
  TOSContainer,
  TOSLink,
} from '../FormWrapper.styled';

import { UserSchema } from '../../../utils/validationSchemas';
import { UserFields } from '../FormWrapper.types';
import { errorMessages } from '../../../utils/errorMessages';

const DEFAULT_TERMS_AND_CONDITIONS =
  'https://www.aula.education/terms-of-service';

type UserSignupStepProps = {
  onSubmit: (data: UserFields) => void;
  loading?: boolean;
  error?: boolean | null;
  spaceName: string;
  termsAndConditionsUrl?: string;
  institutionDisplayName?: string;
};

export const UserSignupStep: React.FC<UserSignupStepProps> = ({
  onSubmit,
  error,
  spaceName,
  loading,
  termsAndConditionsUrl = DEFAULT_TERMS_AND_CONDITIONS,
  institutionDisplayName = 'Aula',
}: UserSignupStepProps) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<UserFields>({
    resolver: yupResolver(UserSchema),
  });

  return (
    <FormWrapper>
      <Title>Sign up for &quot;{spaceName}&quot;:</Title>
      <TextfieldContainer>
        <TextField
          id="signup-form-first-name"
          label="Enter your first name"
          error={!!errors.firstName}
          helperText={errors.firstName?.message}
          {...register('firstName')}
        />
        <TextField
          id="signup-form-last-name"
          label="Enter your last name"
          error={!!errors.lastName}
          helperText={errors.lastName?.message}
          {...register('lastName')}
        />
        <TextField
          id="signup-form-email-address"
          label="Enter your email address"
          error={!!errors.email}
          helperText={errors.email?.message}
          {...register('email')}
        />
      </TextfieldContainer>
      <SubmitContainer>
        {error && (
          <AlertContainer>
            <Alert type="error">{errorMessages.SIGNUP_ERROR}</Alert>
          </AlertContainer>
        )}
        <TOSContainer>
          By clicking “Sign up”, you confirm that you are 18 years or older and
          agree to {institutionDisplayName}’s{' '}
          <TOSLink
            href={termsAndConditionsUrl}
            target="_blank"
            rel="noreferrer"
          >
            T&Cs.
          </TOSLink>
        </TOSContainer>
        <SignupButtonContainer>
          <Button
            id="signup-button"
            type="primary"
            onClick={handleSubmit((data: UserFields) => onSubmit(data))}
            aria-label="Sign up"
            loading={loading}
          >
            Sign up
          </Button>
        </SignupButtonContainer>
      </SubmitContainer>
    </FormWrapper>
  );
};
